import { forwardRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Compass, Calculator, LayoutDashboard } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18n } from '@/contexts/I18nContext';
import { useArtistProfile } from '@/hooks/useArtistData';
import { cn } from '@/lib/utils';

const MobileNav = forwardRef<HTMLElement>(function MobileNav(_props, ref) {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useI18n();
  const { data: profile } = useArtistProfile();

  const navItems = useMemo(() => [
    { path: '/artiesten', icon: Compass, label: t('nav.discover') },
    { path: profile?.slug ? `/c/${profile.slug}` : '/tattoo', icon: Calculator, label: t('nav.my_calculator') },
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.manage') },
  ], [t, profile?.slug]);

  if (!isMobile) return null;

  return (
    <nav ref={ref} className="fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl bg-background border-t border-border safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={cn(
                'relative flex flex-col items-center justify-center gap-1 transition-all duration-200 flex-1 h-full min-w-[64px] min-h-[48px]',
                isActive && 'text-primary'
              )}
            >
              {isActive && (
                <span className="absolute inset-x-2 top-1 bottom-1 rounded-full bg-primary/15 animate-[pulse_3s_ease-in-out_infinite]" />
              )}
              <item.icon className={cn("w-6 h-6 relative z-10 transition-transform", isActive && "scale-110 drop-shadow-[0_0_6px_hsl(var(--primary))]")} />
              <span className={cn("text-[11px] font-medium relative z-10", !isActive && "text-muted-foreground")}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
});

MobileNav.displayName = 'MobileNav';

export default MobileNav;
